import type { HeadInput, HeadTags, LinkTag, MetaTag, SiteConfig } from './types.ts';
import { absoluteUrl } from './url.ts';

/**
 * Sinh TOÀN BỘ thẻ head cho một trang từ một chỗ duy nhất.
 *
 * Vì sao gom vào một hàm thay vì rải `<meta>` trong từng component: mỗi trang thiếu một thẻ là
 * một lỗi SEO âm thầm (không có canonical ⇒ trùng nội dung; thiếu hreflang tự trỏ ⇒ Google bỏ
 * qua cả cụm alternate). Gom lại thì không trang nào thiếu được, và test một lần là phủ mọi trang.
 */
export function buildHeadTags(input: HeadInput, site: SiteConfig): HeadTags {
  const canonical = absoluteUrl(site.url, input.path);
  const image = absoluteUrl(site.url, input.image ?? site.defaultImage);
  const type = input.type ?? 'website';

  const meta: MetaTag[] = [
    { name: 'description', content: input.description },

    // Open Graph — Facebook, Zalo, LinkedIn, và phần lớn công cụ xem trước liên kết.
    { property: 'og:type', content: type },
    { property: 'og:site_name', content: site.name },
    { property: 'og:title', content: input.title },
    { property: 'og:description', content: input.description },
    { property: 'og:url', content: canonical },
    { property: 'og:locale', content: input.ogLocale },
    { property: 'og:image', content: image },
    { property: 'og:image:alt', content: input.imageAlt ?? input.title },

    // Twitter/X — không có các thẻ này thì link hiện thành card trắng trơn.
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: input.title },
    { name: 'twitter:description', content: input.description },
    { name: 'twitter:image', content: image },
  ];

  // og:locale:alternate giúp mạng xã hội biết trang có bản ngôn ngữ khác.
  for (const [hreflang] of Object.entries(input.alternates)) {
    if (hreflang !== input.locale) {
      meta.push({ property: 'og:locale:alternate', content: hreflang });
    }
  }

  if (site.twitterHandle) {
    meta.push({ name: 'twitter:site', content: site.twitterHandle });
  }
  if (site.themeColor) {
    meta.push({ name: 'theme-color', content: site.themeColor });
  }

  meta.push({
    name: 'robots',
    content: input.noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large',
  });

  const links: LinkTag[] = [];

  // Trang noindex KHÔNG khai canonical: vừa nói "đừng index tôi" vừa nói "tôi là bản chuẩn"
  // là hai tín hiệu ngược nhau, Google xử lý không đoán được.
  if (!input.noindex) {
    links.push({ rel: 'canonical', href: canonical });

    // hreflang PHẢI có bản tự trỏ (self-referencing). Thiếu nó Google bỏ qua CẢ cụm alternate.
    for (const [hreflang, path] of Object.entries(input.alternates)) {
      links.push({ rel: 'alternate', hrefLang: hreflang, href: absoluteUrl(site.url, path) });
    }

    const xDefaultPath =
      input.xDefaultLocale === undefined ? undefined : input.alternates[input.xDefaultLocale];
    if (xDefaultPath !== undefined) {
      links.push({
        rel: 'alternate',
        hrefLang: 'x-default',
        href: absoluteUrl(site.url, xDefaultPath),
      });
    }
  }

  return {
    title: input.title,
    meta,
    links,
    canonical: input.noindex ? undefined : canonical,
  };
}
